import graphql__2595cdb1359dff7e16c2 from "./__generated__/ContributionsDatavizViewModeSwitch_participativeModule.graphql.js";
import graphql__406e689c3ae69fef40be from "./__generated__/ContributionsDatavizViewModeSwitch_theme.graphql.js";import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { defineMessages } from "react-intl";
import Switch from "react-switch";
import classNames from "classnames";

import { useFormatMessage } from "../../../Intl/Context";
import { useRoutesContext } from "../../../Routes/RoutesContext";
import KEY_CODES from "../../../Utils/keyCodes";
import Icon from "../../../Utils/Icon";
import ButtonLink from "../../../Buttons/ButtonLink";

const ContributionsDatavizViewModeSwitch = ({
  theme = null,
  graphViewActive = false,
  listViewActive = false,
  ...props
}) => {
  const participativeModule = useFragment(
    ContributionsDatavizViewModeSwitch_participativeModule,
    props.participativeModule
  );
  const participativeModuleSlug = participativeModule.slug;

  theme = useFragment(ContributionsDatavizViewModeSwitch_theme, theme);
  const themeSlug = theme?.slug;

  const { contributionDatavizRootTitleId } = props;

  const graphViewLinkId = "dataviz-switch-graph-view-link";
  const listViewLinkId = "dataviz-switch-list-view-link";

  const t = useFormatMessage();
  const graphViewLabel = t(intlMessages.graphView);
  const listViewLabel = t(intlMessages.listView);

  const [container, setContainer] = useState();

  const navigationParams = useMemo(
    () => ({
      participativeModuleSlug,
      themeSlug
    }),
    [participativeModuleSlug, themeSlug]
  );

  const graphRouteName = theme ? "datavizGraphTheme" : "datavizGraph";
  const listRouteName = theme ? "datavizListTheme" : "datavizList";

  const { pathFor } = useRoutesContext();
  const graphPath = pathFor(graphRouteName, navigationParams);
  const listPath = pathFor(listRouteName, navigationParams);

  const graphLinkRef = useRef();
  const listLinkRef = useRef();
  const toggleTargetLinkRef = graphViewActive ? listLinkRef : graphLinkRef;

  const navigateToToggleTarget = useCallback(
    () => toggleTargetLinkRef.current?.click(),
    [toggleTargetLinkRef]
  );

  const handleKeyDown = useCallback(e => {
    if (e.keyCode === KEY_CODES.LEFT || e.keyCode === KEY_CODES.UP) {
      graphLinkRef.current?.click();
    } else if (e.keyCode === KEY_CODES.RIGHT || e.keyCode === KEY_CODES.DOWN) {
      listLinkRef.current?.click();
    } else {
      return;
    }

    // Avoid arrow keys to make the window scroll
    e.preventDefault();
  }, []);

  const graphSwitchTabClassName = classNames("pointer", {
    disabled: listViewActive,
    "text-primary": graphViewActive
  });

  const switchClassNames = classNames(
    "pointer",
    "switch",
    "react-switch-dataviz-view-mode",
    "d-inline-block",
    "mx-2",
    { left: graphViewActive },
    { right: listViewActive }
  );

  const listSwitchTabClassName = classNames("pointer", {
    disabled: graphViewActive,
    "text-primary": listViewActive
  });

  const switchIcon = (
    <Icon className="icon-validate text-primary d-inline-block rounded-circle" />
  );

  // This effect only removes unwanted WAI-ARIA attributes from the <Switch> input, that
  // we cannot manually set to null by passing it props
  useEffect(() => {
    const input = container?.querySelector("[data-switch-input]");
    if (!input) return;

    input.removeAttribute("role");
    input.removeAttribute("aria-checked");
    input.removeAttribute("aria-labelledby");
  }, [container]);

  return (
    <div
      className="switch-container d-flex align-items-center justify-content-end"
      role="radiogroup"
      aria-labelledby={contributionDatavizRootTitleId}
      onKeyDown={handleKeyDown}
      ref={setContainer}
    >
      <ButtonLink
        id={graphViewLinkId}
        className={graphSwitchTabClassName}
        href={graphPath}
        ref={graphLinkRef}
        role="radio"
        aria-checked={graphViewActive ? "true" : "false"}
        aria-current={graphViewActive && "page"}
        data-dataviz-switch-to-graph-button
      >
        <span className="align-top">{graphViewLabel}</span>&nbsp;
        <Icon className="dataviz-vue-carte align-middle" />
      </ButtonLink>

      <Switch
        onChange={navigateToToggleTarget}
        checked={listViewActive}
        className={switchClassNames}
        width={40}
        height={24}
        handleDiameter={20}
        checkedIcon={false}
        uncheckedIcon={false}
        checkedHandleIcon={switchIcon}
        uncheckedHandleIcon={switchIcon}
        aria-hidden="true"
        tabIndex="-1"
        data-switch-input
      />

      <ButtonLink
        id={listViewLinkId}
        className={listSwitchTabClassName}
        href={listPath}
        ref={listLinkRef}
        role="radio"
        aria-checked={listViewActive ? "true" : "false"}
        aria-current={listViewActive && "page"}
        data-dataviz-switch-to-list-button
      >
        <Icon className="dataviz-vue-liste align-middle" />
        &nbsp;<span className="align-top">{listViewLabel}</span>
      </ButtonLink>
    </div>
  );
};

const ContributionsDatavizViewModeSwitch_participativeModule = graphql__2595cdb1359dff7e16c2;

const ContributionsDatavizViewModeSwitch_theme = graphql__406e689c3ae69fef40be;

export default ContributionsDatavizViewModeSwitch;

const intlMessages = defineMessages({
  graphView: {
    id: "contributions.dataviz.viewModeSwitch.graphView",
    defaultMessage: "Vue carte"
  },
  listView: {
    id: "contributions.dataviz.viewModeSwitch.listView",
    defaultMessage: "Vue liste"
  }
});
